import React from "react";

import { mdiMovieOpenCogOutline } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import { TIME_FORMAT_LONG } from "../../../constants/dateFormat";
import prettyDurations, { DURATION_UNITS } from "../../../utils/durations";
import { FAST_CHANNELS_ROUTE } from "../../routes/routes";
import SidePanel from "../../sidePanel/SidePanel";
import SelectedMediaSidePanelContent from "../scheduling/SelectedMediaSidePanelContent";

import type { JSX } from "react";

import type { CalendarEvent } from "./Calendar";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { SelectedMediaSidePanelFormValues } from "../scheduling/SelectedMediaSidePanel";

interface CalendarWeekEventSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
}

function CalendarWeekEventSidePanel({
  isOpen,
  onClose,
  event,
}: CalendarWeekEventSidePanelProps): JSX.Element | null {
  const { t } = useTranslation("editor-fast-channel");
  const history = useHistory();
  const { channelId } = useParams<FASTChannelRoutesParams>();

  const fastDuration =
    event?.resource.duration &&
    prettyDurations(event.resource.duration, {
      valueUnit: DURATION_UNITS.MILLISECONDS,
    }).fastDuration;
  const prettyStartTime =
    event?.resource.startTime &&
    moment(event.resource.startTime).format(TIME_FORMAT_LONG);
  const prettyEndTime =
    event?.resource.endTime &&
    moment(event.resource.endTime).format(TIME_FORMAT_LONG);

  const methods = useForm<SelectedMediaSidePanelFormValues>({
    values: {
      label: event?.resource.label ?? "",
      duration: fastDuration,
      startTime: prettyStartTime,
      endTime: prettyEndTime,
    },
  });

  if (!event) {
    return null;
  }

  const { label, startTime, endTime, scheduleId, uuid, format, picture } =
    event.resource;

  const title = (
    <div className="flex w-full items-center gap-[0.9375rem] overflow-hidden">
      <Icon path={mdiMovieOpenCogOutline} size={0.9375} />
      <div className="flex text-[1.171875rem]">
        <div className="max-w-[390px] truncate">{label}</div>
      </div>
    </div>
  );

  const startDateTimeStamp = moment(startTime).valueOf();

  const startTimeTimeline = startDateTimeStamp - 1 * 60 * 60 * 1000;
  const endTimeTimeline = startTimeTimeline + 6 * 60 * 60 * 1000;

  const sidePanelActions = [
    {
      label: t("showSchedulingView"),
      id: "monitor-scheduling-view",
      isFullWidth: true,
      isFullHeight: true,
      onClick: (): void => {
        history.push(
          `${FAST_CHANNELS_ROUTE}/${channelId}/scheduling?startTime=${startTimeTimeline}&endTime=${endTimeTimeline}`,
        );
      },
    },
  ];

  return (
    <SidePanel
      actions={sidePanelActions}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
    >
      <FormProvider
        {...methods} // eslint-disable-line react/jsx-props-no-spreading
      >
        <SelectedMediaSidePanelContent
          isStartTimeReadOnly
          endTime={endTime}
          format={format}
          interstitialAdbreaks={event.resource.interstitialAdbreaks}
          picture={picture}
          scheduleId={scheduleId}
          startTime={startTime}
          uuid={uuid}
        />
      </FormProvider>
    </SidePanel>
  );
}

export default CalendarWeekEventSidePanel;
